import axios, { AxiosRequestConfig } from 'axios'
import hash from 'object-hash'

import {
  CompassAPIDispersionDataPoint,
  CompassAPIGiroDataPoint,
  CompassAPIMultiSeriesChartData,
  CompassAPIResultTradingAttDataPoint,
  getCompassAPIChartData,
  getMultipleColumnChartProps,
  getMultipleLineChartProps,
  tooltipFormatterScatter,
} from '../utils/compass'
import { datetimeXAxis, Palette } from '../utils/constants'
import { BaseContext } from './'
import {
  CommonChartConfig,
  commonChartOptions,
  commonDispersionChartOptions,
} from './common'

const PORTFOLIO_ANALYSIS_CHARTS_PATH = '/api/charts/portfolio-analysis'

export interface CompassAPIResultTradingAttChartData {
  data: CompassAPIResultTradingAttDataPoint[]
}

export interface CompassAPIScatterChartData {
  data: {
    id: string
    name: string
    points: CompassAPIDispersionDataPoint[]
  }[]
}

export interface CompassAPIGiroChartData {
  data: CompassAPIGiroDataPoint[]
}

export namespace PortfolioAnalysis {
  export namespace Funds {
    const CONTEXT_ID = 'equity:portfolio_analysis:funds'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'jgp_equity_top_position'
        | 'jgp_equity_average_exposure'
        | 'jgp_equity_sizing_region_cases'
        | 'jgp_equity_sizing_region_exposure'
        | 'jgp_equity_turnover'
        | 'jgp_equity_trading_attribution_yearly_cases'
        | 'jgp_equity_yearly_cases_dispersion'
        | 'jgp_equity_exposure'
        | 'jgp_equity_coverage_dispersion'
        | 'jgp_compounders_top_position'
        | 'jgp_compounders_average_exposure'
        | 'jgp_compounders_sizing_region_cases'
        | 'jgp_compounders_sizing_region_exposure'
        | 'jgp_compounders_turnover'
        | 'jgp_compounders_trading_attribution_yearly_cases'
        | 'jgp_compounders_yearly_cases_dispersion'
        | 'jgp_compounders_exposure'
        | 'jgp_compounders_coverage_dispersion'
        | 'jgp_esg_fia_top_position'
        | 'jgp_esg_fia_average_exposure'
        | 'jgp_esg_fia_sizing_region_cases'
        | 'jgp_esg_fia_sizing_region_exposure'
        | 'jgp_esg_fia_turnover'
        | 'jgp_esg_fia_trading_attribution_yearly_cases'
        | 'jgp_esg_fia_yearly_cases_dispersion'
        | 'jgp_esg_fia_exposure'
        | 'jgp_esg_fia_coverage_dispersion'
      context: typeof CONTEXT_ID
      params?: Partial<AxiosRequestConfig>
    }

    export type ChartData =
      | CompassAPIMultiSeriesChartData
      | CompassAPIResultTradingAttChartData
      | CompassAPIScatterChartData

    export const getChartData = (
      config: ChartConfig,
      requestModifier: (config: AxiosRequestConfig) => AxiosRequestConfig = (
        conf
      ) => conf
    ) =>
      getCompassAPIChartData(config, (conf) =>
        requestModifier({ ...conf, params: config.params ?? undefined })
      )

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'jgp_equity_top_position':
          case 'jgp_equity_average_exposure':
          case 'jgp_equity_turnover':
          case 'jgp_compounders_top_position':
          case 'jgp_compounders_average_exposure':
          case 'jgp_compounders_turnover':
          case 'jgp_esg_fia_top_position':
          case 'jgp_esg_fia_average_exposure':
          case 'jgp_esg_fia_turnover':
            return getChartProps(config, data as CompassAPIMultiSeriesChartData)
          case 'jgp_equity_sizing_region_cases':
          case 'jgp_equity_sizing_region_exposure':
          case 'jgp_compounders_sizing_region_cases':
          case 'jgp_compounders_sizing_region_exposure':
          case 'jgp_esg_fia_sizing_region_cases':
          case 'jgp_esg_fia_sizing_region_exposure':
            return getAreaChartProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
          case 'jgp_equity_trading_attribution_yearly_cases':
          case 'jgp_compounders_trading_attribution_yearly_cases':
          case 'jgp_esg_fia_trading_attribution_yearly_cases':
            return getColumnChartProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
          case 'jgp_equity_yearly_cases_dispersion':
          case 'jgp_equity_coverage_dispersion':
          case 'jgp_compounders_yearly_cases_dispersion':
          case 'jgp_compounders_coverage_dispersion':
          case 'jgp_esg_fia_yearly_cases_dispersion':
          case 'jgp_esg_fia_coverage_dispersion':
            return getScatterChartProps(
              config,
              data as CompassAPIScatterChartData
            )
          case 'jgp_equity_exposure':
          case 'jgp_compounders_exposure':
          case 'jgp_esg_fia_exposure':
            return getSingleColumnChartProps(
              config,
              data as CompassAPIResultTradingAttChartData
            )
        }
      },
    }

    const getChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          convertToPercentages: Array(data.data.length).fill(true),
        },
        {
          ...(config.chart.includes('_equity_top_position')
            ? {
                color: [
                  Palette.darkBlue,
                  Palette.gray,
                  Palette.darkGreen,
                  Palette.gray,
                ],
              }
            : {}),
          type: Array(data.data.length).fill('spline'),
        }
      )(config, data)

    const getAreaChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleColumnChartProps(
        Context,
        {
          ...(config.chart.includes('_sizing_region_exposure')
            ? {
                convertToPercentages: Array(data.data.length).fill(true),
              }
            : {}),
          plotOptions: { area: { stacking: 'normal' } },
        },
        {
          type: Array(data.data.length).fill('area'),
        }
      )(config, data)

    const getColumnChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleColumnChartProps(
        Context,
        {
          convertToPercentages: Array(data.data.length).fill(true),
          plotOptions: { column: { pointPlacement: 'between' } },
        },
        {
          type: Array(data.data.length).fill('column'),
        }
      )(config, data)

    const getScatterChartProps = (
      config: ChartConfig,
      data: CompassAPIScatterChartData
    ) => {
      const series = data.data.map((s, idx) => ({
        animation: false,
        id: `series-${idx}`,
        name: s.name ?? undefined,
        label: s.points[idx].label ?? undefined,
        data: s.points.map((point: any) => ({
          x: point.x * 100,
          y: point.y * 100,
          label: point.label,
        })),
      }))

      const tooltip = {
        backgroundColor: 'rgba(247,247,247,0.85)',
        enabled: true,
        formatter: tooltipFormatterScatter({
          xAxisSuffix: '%',
          yAxisSuffix: '%',
          xAxisLabel: 'Avg Exposure (Std/Avg)',
          yAxisLabel: 'Trading attribution',
        }),
        shared: false,
        style: {
          color: '#333333',
          cursor: 'default',
          fontSize: '12px',
          whiteSpace: 'nowrap',
        },
        useHTML: true,
        zIndex: 1000,
      }

      return {
        id: Context.getChartId(config),
        title: config.chart.includes('_yearly_cases_dispersion')
          ? 'Yearly cases dispersion'
          : 'Coverage dispersion',
        options: {
          ...commonChartOptions,
          ...commonDispersionChartOptions,
          credits: { text: 'Fonte: JGP' },
          series,
          tooltip,
          xAxis: {
            title: { text: 'Avg Exposure (Std/Avg)' },
            labels: { format: '{value}%' },
          },
          yAxis: {
            title: { text: 'Trading attribution' },
            labels: { format: '{value}%' },
          },
        },
        stockTools: false,
        useRegularChart: true,
      }
    }

    const getSingleColumnChartProps = (
      config: ChartConfig,
      data: CompassAPIResultTradingAttChartData
    ) => {
      const series = [
        {
          id: `series-1`,
          type: 'column',
          name: '',
          data: data.data.map((s) => [s.result_date, s.trading_att * 100]),
          dataLabels: { enabled: true, format: '{y:.2f}%' },
          showInLegend: false,
          yAxis: 'default',
        },
      ]

      const yAxis = [
        {
          id: 'default',
          opposite: false,
          labels: { format: '{value}%' },
          title: { text: '' },
        },
      ]

      return {
        id: Context.getChartId(config),
        title: 'Trading attribution - Coverage cases',
        options: {
          ...commonChartOptions,
          credits: { text: 'Fonte: JGP' },
          series,
          stockTools: { gui: { enabled: false } },
          tooltip: {
            shared: true,
            split: undefined,
            valueDecimals: 2,
            valueSuffix: '%',
          },
          yAxis,
        },
        stockTools: false,
        useRegularChart: true,
      }
    }
  }

  export namespace Comparison {
    const CONTEXT_ID = 'equity:portfolio_analysis:comparison'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'jgp_equity_fund_performance'
        | 'jgp_esg_fund_performance'
        | 'jgp_compounders_fund_performance'
        | 'jgp_equity_jgp_intersection'
        | 'jgp_compounders_jgp_intersection'
        | 'jgp_esg_fia_jgp_intersection'
        | 'jgp_giro'
        | 'cvm_giro'
      context: typeof CONTEXT_ID
      params?: Partial<AxiosRequestConfig>
    }

    export type ChartData =
      | CompassAPIMultiSeriesChartData
      | CompassAPIGiroChartData

    export const getChartData = (
      config: ChartConfig,
      requestModifier: (config: AxiosRequestConfig) => AxiosRequestConfig = (
        conf
      ) => conf
    ) =>
      getCompassAPIChartData(config, (conf) =>
        requestModifier({ ...conf, params: config.params ?? undefined })
      )

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'jgp_equity_fund_performance':
          case 'jgp_esg_fund_performance':
          case 'jgp_compounders_fund_performance':
          case 'jgp_equity_jgp_intersection':
          case 'jgp_compounders_jgp_intersection':
          case 'jgp_esg_fia_jgp_intersection':
            return getChartProps(config, data as CompassAPIMultiSeriesChartData)
          case 'jgp_giro':
          case 'cvm_giro':
            return getSingleColumnChartProps(
              config,
              data as CompassAPIGiroChartData
            )
        }
      },
    }

    const getChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          chartTitle:
            config.chart === 'jgp_equity_fund_performance'
              ? 'JGP Equity performance'
              : config.chart === 'jgp_esg_fund_performance'
              ? 'JGP ESG performance'
              : config.chart === 'jgp_compounders_fund_performance'
              ? 'JGP Compounders performance'
              : config.chart === 'jgp_equity_jgp_intersection'
              ? 'JGP Equity intersection'
              : config.chart === 'jgp_compounders_jgp_intersection'
              ? 'JGP Compounders intersection'
              : 'JGP ESG FIA intersection',
          convertToPercentages: Array(data.data.length).fill(true),
        },
        { type: Array(data.data.length).fill('spline') }
      )(config, data)

    const getSingleColumnChartProps = (
      config: ChartConfig,
      data: CompassAPIGiroChartData
    ) => {
      const series = [
        {
          id: `series-1`,
          type: 'column',
          name: '',
          data: data.data
            .map((s): [string, number] =>
              s.fund === 'jgp_equity'
                ? ['JGP Equity', s.giro]
                : s.fund === 'jgp_esg_fia'
                ? ['JGP ESG FIA', s.giro]
                : s.fund === 'jgp_compounders'
                ? ['JGP Compounders', s.giro]
                : [s.fund, s.giro]
            )
            .sort((a, b) => b[1] - a[1]),
          dataLabels: { enabled: true, format: '{y:.2f}' },
          showInLegend: false,
          yAxis: 'default',
        },
      ]

      const tooltip = { shared: true, split: undefined, valueDecimals: 2 }

      const xAxis = [{ categories: series[0].data.map((el) => el[0]) }]

      const yAxis = [{ id: 'default', opposite: false, title: { text: '' } }]

      return {
        id: Context.getChartId(config),
        title: `${config.chart === 'jgp_giro' ? 'JGP' : 'CVM'} Turnover`,
        options: {
          ...commonChartOptions,
          chart: { ...commonChartOptions.chart, type: 'bar' },
          credits: { text: 'Fonte: JGP' },
          series,
          stockTools: { gui: { enabled: false } },
          tooltip,
          xAxis,
          yAxis,
        },
        stockTools: false,
        useRegularChart: true,
      }
    }
  }

  export namespace GroupedCases {
    const CONTEXT_ID = 'equity:portfolio_analysis:grouped_cases'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    type FundsComponentId = 'cases' | 'yearly_cases' | 'coverage_cases'

    export interface ChartConfig {
      title?: string
      resultDate: number | null
      chart: FundsComponentId
      fundKey: string
      caseName: string
      context: typeof CONTEXT_ID
    }

    type ChartData = CompassAPIMultiSeriesChartData

    const FUNDS_CHARTS_PATHS: Record<FundsComponentId, string> = {
      cases: '/funds/case-infos',
      yearly_cases: '/funds/yearly-case-infos',
      coverage_cases: '/funds/coverage-ticker-info',
    }

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: PORTFOLIO_ANALYSIS_CHARTS_PATH + FUNDS_CHARTS_PATHS[config.chart],
        method: 'POST',
        data: {
          fund_key: config.fundKey,
          result_date: config.resultDate,
          ...(config.chart === 'coverage_cases'
            ? {
                principal_name: config.caseName,
              }
            : { case_name: config.caseName }),
        },
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(
        Context,
        {
          chartTitle: config.caseName,
          convertToPercentages: [
            false,
            ...Array(data.data.length - 1).fill(true),
          ],
          yAxis: [
            { id: 'default', opposite: false, title: { text: '' } },
            {
              id: 'percentual',
              opposite: true,
              title: { text: '' },
              labelSuffix: '%',
            },
          ],
        },
        {
          yAxis: ['default', ...Array(data.data.length - 1).fill('percentual')],
          type: Array(data.data.length).fill('spline'),
        }
      )(config, data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace Risk {
    const CONTEXT_ID = 'equity:portfolio_analysis:risk'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    type TimeIntervalId =
      | '3_months'
      | '6_months'
      | '12_months'
      | '24_months'
      | '36_months'
      | '60_months'
      | '120_months'

    type MetricId =
      | 'drawdown'
      | 'max_drawdown'
      | 'annualized_volatility'
      | 'annualized_return'
      | 'excess_return'
      | 'annualized_excess_return'
      | 'sharpe_ratio'
      | 'calmar_ratio'
      | 'sortino_ratio'

    export interface ChartConfig {
      title: string
      funds: string[]
      intervalTime: TimeIntervalId
      metric: MetricId
      context: typeof CONTEXT_ID
    }

    type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url:
          PORTFOLIO_ANALYSIS_CHARTS_PATH + '/comparison/risk-series-by-funds',
        method: 'POST',
        data: {
          funds: config.funds,
          interval_time: config.intervalTime,
          metric: config.metric,
        },
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(
        Context,
        {
          chartTitle: config.title,
          convertToPercentages: Array(data.data.length).fill(true),
          useRegularChart: true,
          yAxis: [
            {
              id: 'percentual',
              opposite: true,
              title: { text: '' },
              labelSuffix: '%',
            },
          ],
          xAxis: datetimeXAxis[0],
        },
        {
          type: Array(data.data.length).fill('spline'),
          yAxis: Array(data.data.length).fill('percentual'),
        }
      )(config, data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${hash(config, { unorderedArrays: true })}`,
      getChartData,
      getChartProps,
    }
  }
}
